/**
 * NeuNexus Energy — Uetze Consortium Executive Pitch
 * Native, code-rendered slides (no image dumps).
 * Content fully localised in PT-PT / EN / DE.
 *
 * Each slide has:
 *   - kind: layout template used by index.jsx
 *   - id:   stable slug
 *   - n:    display number (1-based)
 *   - i18n: { pt: {...}, en: {...}, de: {...} }
 */

export const SLIDES = [
  // -------------------------------------------------------------- 1
  {
    kind: "title",
    id: "cover",
    n: 1,
    i18n: {
      pt: {
        eyebrow: "Consórcio Uetze · Blueprint Indústria 4.0",
        title: "NeuNexus Energy",
        subtitle: "Ecossistema Integrado de Energia, Automação e Precisão para o Futuro Industrial Europeu.",
        tag: "Confidencial · Apresentação Executiva · Q1 2026",
      },
      en: {
        eyebrow: "Uetze Consortium · Industry 4.0 Blueprint",
        title: "NeuNexus Energy",
        subtitle: "An integrated ecosystem of energy, automation and precision for Europe's industrial future.",
        tag: "Confidential · Executive Briefing · Q1 2026",
      },
      de: {
        eyebrow: "Uetze-Konsortium · Industrie 4.0 Blaupause",
        title: "NeuNexus Energy",
        subtitle: "Integriertes Ökosystem für Energie, Automatisierung und Präzision für die industrielle Zukunft Europas.",
        tag: "Vertraulich · Executive-Präsentation · Q1 2026",
      },
    },
  },

  // -------------------------------------------------------------- 2
  {
    kind: "challenge",
    id: "energiewende",
    n: 2,
    i18n: {
      pt: {
        eyebrow: "O Desafio",
        title: "A Transição Energética já não é opcional.",
        lede: "A indústria europeia enfrenta pressão simultânea sobre custos, carbono e competitividade — sem uma resposta integrada, perde soberania.",
        points: [
          { k: "+312%", v: "aumento do preço industrial da energia (2019–2024)" },
          { k: "2030",  v: "prazo obrigatório de descarbonização para instalações fabris (UE)" },
          { k: "−22%",  v: "quota da indústria transformadora alemã em risco até 2035" },
          { k: "0",     v: "consórcios verticalmente integrados em produção regional a operar hoje" },
        ],
      },
      en: {
        eyebrow: "The Challenge",
        title: "The Energiewende is no longer optional.",
        lede: "European industry is squeezed by cost, carbon and competitiveness — without an integrated answer, sovereignty is lost.",
        points: [
          { k: "+312%", v: "industrial energy price increase (2019–2024)" },
          { k: "2030",  v: "mandatory decarbonisation deadline for EU manufacturing sites" },
          { k: "−22%",  v: "German manufacturing share at risk by 2035" },
          { k: "0",     v: "vertically-integrated regional consortia operating today" },
        ],
      },
      de: {
        eyebrow: "Die Herausforderung",
        title: "Die Energiewende ist nicht mehr optional.",
        lede: "Die europäische Industrie steht gleichzeitig unter Kosten-, CO₂- und Wettbewerbsdruck — ohne integrierte Antwort geht Souveränität verloren.",
        points: [
          { k: "+312%", v: "Anstieg der industriellen Energiepreise (2019–2024)" },
          { k: "2030",  v: "verbindliche Dekarbonisierungsfrist für EU-Produktionsstandorte" },
          { k: "−22%",  v: "gefährdeter Anteil der deutschen Fertigung bis 2035" },
          { k: "0",     v: "vertikal integrierte, regionale Konsortien heute im Betrieb" },
        ],
      },
    },
  },

  // -------------------------------------------------------------- 3
  {
    kind: "location",
    id: "uetze-site",
    n: 3,
    i18n: {
      pt: {
        eyebrow: "Ponto de Partida",
        title: "Smart Factory Uetze — Baixa Saxónia.",
        lede: "Um terreno industrial de 12 hectares, com acesso a ferrovia e ao Canal do Mittelland, pronto para acolher o núcleo do consórcio.",
        facts: [
          { k: "Localização",         v: "Uetze, Niedersachsen, DE" },
          { k: "Área disponível",     v: "12 ha (fase 1) + 8 ha (fase 2)" },
          { k: "Ligação logística",   v: "Ferrovia · A2 · Mittellandkanal" },
          { k: "Rede elétrica",       v: "110 kV · subestação dedicada" },
          { k: "Estado",              v: "Licenças preliminares aprovadas" },
        ],
      },
      en: {
        eyebrow: "Starting Point",
        title: "Smart Factory Uetze — Lower Saxony.",
        lede: "A 12-hectare industrial site with rail and Mittellandkanal access, ready to host the consortium's core.",
        facts: [
          { k: "Location",           v: "Uetze, Niedersachsen, DE" },
          { k: "Available area",     v: "12 ha (phase 1) + 8 ha (phase 2)" },
          { k: "Logistics",          v: "Rail · A2 motorway · Mittellandkanal" },
          { k: "Grid connection",    v: "110 kV · dedicated substation" },
          { k: "Status",             v: "Preliminary permits approved" },
        ],
      },
      de: {
        eyebrow: "Ausgangspunkt",
        title: "Smart Factory Uetze — Niedersachsen.",
        lede: "Ein 12 Hektar großer Industriestandort mit Schienen- und Mittellandkanal-Anbindung, bereit als Herz des Konsortiums.",
        facts: [
          { k: "Standort",           v: "Uetze, Niedersachsen, DE" },
          { k: "Verfügbare Fläche",  v: "12 ha (Phase 1) + 8 ha (Phase 2)" },
          { k: "Logistik",           v: "Schiene · A2 · Mittellandkanal" },
          { k: "Netzanschluss",      v: "110 kV · eigenes Umspannwerk" },
          { k: "Status",             v: "Vorabgenehmigungen erteilt" },
        ],
      },
    },
  },

  // -------------------------------------------------------------- 4
  {
    kind: "kpis",
    id: "impact",
    n: 4,
    i18n: {
      pt: {
        eyebrow: "Métricas de Impacto",
        title: "Um caso de investimento comprovado.",
        lede: "Impacto ambiental, industrial e social medido em cada camada do consórcio.",
        kpis: [
          { v: "−68%",   l: "Custo energético industrial", s: "vs. mix atual DE" },
          { v: "−84 kt", l: "CO₂ evitado por ano",         s: "após ano 3 de operação" },
          { v: "+320",   l: "Empregos diretos criados",    s: "engenharia · operação · manutenção" },
          { v: "4,2 a",  l: "Payback do consórcio",        s: "sob cenário base UE" },
          { v: "€280 M", l: "Produto Regional Bruto",      s: "injeção acumulada em 10 anos" },
          { v: "99,98%", l: "Disponibilidade operacional", s: "arquitetura tri-redundante" },
        ],
      },
      en: {
        eyebrow: "Impact Metrics",
        title: "A hardened investment case.",
        lede: "Environmental, industrial and social impact measured at every layer of the consortium.",
        kpis: [
          { v: "−68%",   l: "Industrial energy cost",     s: "vs. current DE grid mix" },
          { v: "−84 kt", l: "CO₂ avoided per year",       s: "from operational year 3" },
          { v: "+320",   l: "Direct jobs created",        s: "engineering · ops · maintenance" },
          { v: "4.2 y",  l: "Consortium payback",         s: "EU base scenario" },
          { v: "€280 M", l: "Regional gross product",     s: "cumulative over 10 years" },
          { v: "99.98%", l: "Operational uptime",         s: "triple-redundant architecture" },
        ],
      },
      de: {
        eyebrow: "Wirkungsmetriken",
        title: "Ein belastbarer Investment-Case.",
        lede: "Umwelt-, Industrie- und Sozialwirkung, gemessen auf jeder Ebene des Konsortiums.",
        kpis: [
          { v: "−68%",   l: "Industrieller Energiepreis", s: "ggü. aktuellem DE-Strommix" },
          { v: "−84 kt", l: "CO₂-Vermeidung pro Jahr",    s: "ab Betriebsjahr 3" },
          { v: "+320",   l: "Direkte Arbeitsplätze",      s: "Engineering · Betrieb · Instandhaltung" },
          { v: "4,2 J",  l: "Payback des Konsortiums",    s: "EU-Basisszenario" },
          { v: "€280 M", l: "Bruttoregionalprodukt",      s: "kumuliert über 10 Jahre" },
          { v: "99,98%", l: "Betriebsverfügbarkeit",      s: "dreifach redundante Architektur" },
        ],
      },
    },
  },

  // -------------------------------------------------------------- 5
  {
    kind: "layer",
    id: "layer-siemens",
    n: 5,
    partner: "Siemens",
    accent: "#00A5FF",
    i18n: {
      pt: {
        eyebrow: "Camada 1",
        title: "Engenharia de Potência — Siemens.",
        lede: "Base energética e de controlo do consórcio: geração descentralizada, estabilização de rede e monitorização soberana.",
        pillars: [
          { t: "Geração híbrida",  d: "50 MW solar + eólico + armazenamento em baterias BESS." },
          { t: "SIMATIC & MindSphere", d: "Automação e nuvem industrial em conformidade ISO 27001/50001." },
          { t: "Estabilização de rede", d: "Serviços auxiliares para o TSO nacional (Tennet)." },
          { t: "Soberania de dados", d: "Todos os dados operacionais residem em cloud europeia." },
        ],
      },
      en: {
        eyebrow: "Layer 1",
        title: "Power Engineering — Siemens.",
        lede: "The energy and control backbone of the consortium: decentralised generation, grid stabilisation and sovereign monitoring.",
        pillars: [
          { t: "Hybrid generation",     d: "50 MW solar + wind + BESS battery storage." },
          { t: "SIMATIC & MindSphere",  d: "Industrial automation & cloud in ISO 27001/50001 compliance." },
          { t: "Grid stabilisation",    d: "Ancillary services delivered to the national TSO (Tennet)." },
          { t: "Data sovereignty",      d: "All operational data hosted in European cloud." },
        ],
      },
      de: {
        eyebrow: "Ebene 1",
        title: "Leistungstechnik — Siemens.",
        lede: "Das Energie- und Steuerungsrückgrat des Konsortiums: dezentrale Erzeugung, Netzstabilisierung und souveräne Überwachung.",
        pillars: [
          { t: "Hybride Erzeugung",     d: "50 MW Solar + Wind + BESS-Batteriespeicher." },
          { t: "SIMATIC & MindSphere",  d: "Industrielle Automatisierung & Cloud gemäß ISO 27001/50001." },
          { t: "Netzstabilisierung",    d: "Systemdienstleistungen für den nationalen ÜNB (Tennet)." },
          { t: "Datensouveränität",     d: "Alle Betriebsdaten in europäischer Cloud." },
        ],
      },
    },
  },

  // -------------------------------------------------------------- 6
  {
    kind: "twin",
    id: "digital-twin",
    n: 6,
    accent: "#00A5FF",
    i18n: {
      pt: {
        eyebrow: "Gémeo Digital Siemens",
        title: "Risco de execução operacional — zero.",
        lede: "Antes de qualquer parafuso ser apertado em Uetze, todo o consórcio é construído, testado e otimizado no gémeo digital Siemens.",
        stages: [
          { t: "Réplica virtual",   d: "Modelação completa do site com Xcelerator." },
          { t: "Simulação",         d: "Testes de carga, falha e manutenção durante 10 000 horas virtuais." },
          { t: "Otimização IA",     d: "Redução de consumo e desgaste antes da construção." },
          { t: "Operação-espelho",  d: "Depois de arrancar, o gémeo continua a operar em tempo real." },
        ],
      },
      en: {
        eyebrow: "Siemens Digital Twin",
        title: "Zero operational execution risk.",
        lede: "Before a single bolt is tightened in Uetze, the entire consortium is built, stress-tested and optimised inside the Siemens digital twin.",
        stages: [
          { t: "Virtual replica",   d: "Full site modelling using Siemens Xcelerator." },
          { t: "Simulation",        d: "Load, fault and maintenance tests over 10,000 virtual hours." },
          { t: "AI optimisation",   d: "Consumption and wear reduced before construction begins." },
          { t: "Live shadowing",    d: "Once online, the twin keeps running in real time." },
        ],
      },
      de: {
        eyebrow: "Siemens Digital Twin",
        title: "Null operatives Ausführungsrisiko.",
        lede: "Bevor in Uetze eine einzige Schraube angezogen wird, wird das gesamte Konsortium im Siemens Digital Twin gebaut, belastet und optimiert.",
        stages: [
          { t: "Virtuelles Abbild",   d: "Komplette Standortmodellierung mit Siemens Xcelerator." },
          { t: "Simulation",          d: "Last-, Fehler- und Wartungstests über 10 000 virtuelle Stunden." },
          { t: "KI-Optimierung",      d: "Verbrauch und Verschleiß werden vor Baubeginn reduziert." },
          { t: "Live-Shadowing",      d: "Nach dem Start läuft der Twin in Echtzeit weiter." },
        ],
      },
    },
  },

  // -------------------------------------------------------------- 7
  {
    kind: "layer",
    id: "layer-vcow",
    n: 7,
    partner: "VCOW",
    accent: "#14FF8C",
    i18n: {
      pt: {
        eyebrow: "Camada 2",
        title: "Cérebro Físico da Automação — VCOW.",
        lede: "Controladores, atuadores e lógica cinemática que executam em tempo real cada instrução vinda da camada Siemens.",
        pillars: [
          { t: "PLC & Motion",           d: "Controlo determinístico com latência < 250 µs." },
          { t: "Robótica colaborativa",  d: "Braços de 6 e 7 eixos certificados ISO/TS 15066." },
          { t: "Barramento redundante",  d: "PROFINET IRT em dupla anilha — falha isolada." },
          { t: "Manutenção preditiva",   d: "Modelo de saúde de cada eixo enviado ao gémeo digital." },
        ],
      },
      en: {
        eyebrow: "Layer 2",
        title: "Physical Automation Core — VCOW.",
        lede: "The controllers, actuators and kinematic logic that execute in real time every instruction from the Siemens layer.",
        pillars: [
          { t: "PLC & Motion",             d: "Deterministic control with < 250 µs latency." },
          { t: "Collaborative robotics",   d: "6- and 7-axis arms certified to ISO/TS 15066." },
          { t: "Redundant bus",            d: "PROFINET IRT double-ring — faults are isolated." },
          { t: "Predictive maintenance",   d: "Per-axis health model streamed to the digital twin." },
        ],
      },
      de: {
        eyebrow: "Ebene 2",
        title: "Physisches Automatisierungszentrum — VCOW.",
        lede: "Die Steuerungen, Aktoren und die kinematische Logik, die jede Anweisung der Siemens-Ebene in Echtzeit ausführen.",
        pillars: [
          { t: "PLC & Motion",             d: "Deterministische Steuerung mit < 250 µs Latenz." },
          { t: "Kollaborative Robotik",    d: "6- und 7-Achsen-Arme nach ISO/TS 15066 zertifiziert." },
          { t: "Redundanter Bus",          d: "PROFINET IRT als Doppelring — Fehler bleiben isoliert." },
          { t: "Predictive Maintenance",   d: "Achsenzustandsmodell live an den Digital Twin." },
        ],
      },
    },
  },

  // -------------------------------------------------------------- 8
  {
    kind: "redundancy",
    id: "hardware-redundancy",
    n: 8,
    accent: "#14FF8C",
    i18n: {
      pt: {
        eyebrow: "Arquitetura de Hardware",
        title: "À prova de falhas por desenho.",
        lede: "Cada função crítica é executada por três caminhos independentes. Uma falha nunca produz paragem operacional.",
        rings: [
          { t: "Anilha A", d: "Controlo primário — fibra dedicada." },
          { t: "Anilha B", d: "Controlo secundário — fibra alternativa geograficamente separada." },
          { t: "Anilha C", d: "Voto de segurança — SIL-3 · reinicia diretamente qualquer discrepância." },
        ],
        standards: ["ISO 26262 ASIL-D", "IEC 61508 SIL-3", "IEC 62443-3-3 (OT Cyber)"],
      },
      en: {
        eyebrow: "Hardware Architecture",
        title: "Fail-proof by design.",
        lede: "Every critical function runs across three independent paths. A single fault never yields downtime.",
        rings: [
          { t: "Ring A", d: "Primary control — dedicated fibre." },
          { t: "Ring B", d: "Secondary control — geographically separated fibre." },
          { t: "Ring C", d: "Safety voter — SIL-3 · resolves any discrepancy." },
        ],
        standards: ["ISO 26262 ASIL-D", "IEC 61508 SIL-3", "IEC 62443-3-3 (OT Cyber)"],
      },
      de: {
        eyebrow: "Hardware-Architektur",
        title: "Ausfallsicher konstruiert.",
        lede: "Jede kritische Funktion läuft über drei unabhängige Pfade. Ein Einzelausfall führt niemals zu Stillstand.",
        rings: [
          { t: "Ring A", d: "Primäre Steuerung — dedizierte Glasfaser." },
          { t: "Ring B", d: "Sekundäre Steuerung — geografisch getrennte Glasfaser." },
          { t: "Ring C", d: "Sicherheits-Voter — SIL-3 · löst jede Diskrepanz auf." },
        ],
        standards: ["ISO 26262 ASIL-D", "IEC 61508 SIL-3", "IEC 62443-3-3 (OT-Cyber)"],
      },
    },
  },

  // -------------------------------------------------------------- 9
  {
    kind: "layer",
    id: "layer-dfoptix",
    n: 9,
    partner: "DFOPTIX",
    accent: "#FFB800",
    i18n: {
      pt: {
        eyebrow: "Camada 3",
        title: "Visão Robótica — DFOPTIX.",
        lede: "Metrologia ótica de alta velocidade e IA de inspeção: os olhos do consórcio, a operar acima do humano.",
        pillars: [
          { t: "Óptica multi-espectro",   d: "Sensores VIS + IR + hiperspectral em linha de produção." },
          { t: "IA de inspeção",          d: "Classificação de defeitos < 5 ms por amostra." },
          { t: "Precisão sub-micron",     d: "Tolerância 5 µm em peças metálicas fundidas." },
          { t: "Aprendizagem contínua",   d: "Cada peça inspeciona-se e ensina o modelo seguinte." },
        ],
      },
      en: {
        eyebrow: "Layer 3",
        title: "Robotic Vision — DFOPTIX.",
        lede: "High-speed optical metrology and inspection AI: the consortium's eyes, operating beyond human capability.",
        pillars: [
          { t: "Multi-spectral optics",    d: "VIS + IR + hyperspectral sensors on the production line." },
          { t: "Inspection AI",            d: "Defect classification in < 5 ms per sample." },
          { t: "Sub-micron precision",     d: "5 µm tolerance on cast metal components." },
          { t: "Continuous learning",      d: "Every inspected part trains the next model." },
        ],
      },
      de: {
        eyebrow: "Ebene 3",
        title: "Robotische Vision — DFOPTIX.",
        lede: "Hochgeschwindigkeits-Optikmesstechnik und Inspektions-KI: die Augen des Konsortiums, weit über menschliche Fähigkeiten hinaus.",
        pillars: [
          { t: "Multispektrale Optik",     d: "VIS-, IR- und hyperspektrale Sensoren in der Produktion." },
          { t: "Inspektions-KI",           d: "Fehlerklassifikation in < 5 ms pro Probe." },
          { t: "Sub-Mikron-Präzision",     d: "5 µm Toleranz an Metallgussteilen." },
          { t: "Kontinuierliches Lernen",  d: "Jedes geprüfte Teil trainiert das nächste Modell." },
        ],
      },
    },
  },

  // -------------------------------------------------------------- 10
  {
    kind: "leap",
    id: "precision-leap",
    n: 10,
    accent: "#FFB800",
    i18n: {
      pt: {
        eyebrow: "Salto de Precisão DFOPTIX",
        title: "De manual para exponencial.",
        lede: "Comparação directa entre o método clássico e o pipeline DFOPTIX na mesma linha de produção.",
        rows: [
          { m: "Tempo por peça",       a: "42 s (manual)",       b: "0,11 s (DFOPTIX)" },
          { m: "Precisão de deteção",  a: "94,1 %",              b: "99,98 %" },
          { m: "Custo de reclamação",  a: "€12,40 / peça",       b: "€0,18 / peça" },
          { m: "Rastreabilidade",      a: "Papel · lote",        b: "Digital · unidade" },
          { m: "Escalabilidade",       a: "Linear ao pessoal",   b: "Exponencial ao software" },
        ],
      },
      en: {
        eyebrow: "DFOPTIX Precision Leap",
        title: "From manual to exponential.",
        lede: "Direct comparison between the classical method and the DFOPTIX pipeline on the same production line.",
        rows: [
          { m: "Time per part",        a: "42 s (manual)",       b: "0.11 s (DFOPTIX)" },
          { m: "Detection accuracy",   a: "94.1 %",              b: "99.98 %" },
          { m: "Claim cost",           a: "€12.40 / part",       b: "€0.18 / part" },
          { m: "Traceability",         a: "Paper · batch",       b: "Digital · per unit" },
          { m: "Scalability",          a: "Linear to headcount", b: "Exponential to software" },
        ],
      },
      de: {
        eyebrow: "DFOPTIX Präzisionssprung",
        title: "Von manuell zu exponentiell.",
        lede: "Direkter Vergleich zwischen klassischer Methode und DFOPTIX-Pipeline an derselben Fertigungslinie.",
        rows: [
          { m: "Zeit pro Teil",          a: "42 s (manuell)",         b: "0,11 s (DFOPTIX)" },
          { m: "Erkennungsgenauigkeit",  a: "94,1 %",                 b: "99,98 %" },
          { m: "Reklamationskosten",     a: "€12,40 / Teil",          b: "€0,18 / Teil" },
          { m: "Rückverfolgbarkeit",     a: "Papier · Charge",        b: "Digital · pro Einheit" },
          { m: "Skalierbarkeit",         a: "Linear zum Personal",    b: "Exponentiell zur Software" },
        ],
      },
    },
  },

  // -------------------------------------------------------------- 11
  {
    kind: "facility",
    id: "calibration",
    n: 11,
    i18n: {
      pt: {
        eyebrow: "Centro de Calibração de Uetze",
        title: "Ultra-precisão certificada.",
        lede: "Instalação ISO/IEC 17025 servindo o consórcio e terceiros (indústria automóvel, aeroespacial e médica).",
        cards: [
          { t: "Sala classe ISO 5",         d: "0,1 K de estabilidade térmica, vibrações < 1 µm/s." },
          { t: "Interferometria laser",     d: "Referências rastreáveis ao PTB (Braunschweig)." },
          { t: "Metrologia CT industrial",  d: "Inspeção interna sem contacto de peças complexas." },
          { t: "Serviço externo",           d: "Aberto a clientes B2B na região a partir do ano 2." },
        ],
      },
      en: {
        eyebrow: "Uetze Calibration Centre",
        title: "Certified ultra-precision.",
        lede: "ISO/IEC 17025 facility serving the consortium and third parties (automotive, aerospace and medical).",
        cards: [
          { t: "ISO 5 clean room",         d: "0.1 K thermal stability, vibrations < 1 µm/s." },
          { t: "Laser interferometry",     d: "References traceable to PTB Braunschweig." },
          { t: "Industrial CT metrology",  d: "Non-contact internal inspection of complex parts." },
          { t: "External service",         d: "Open to regional B2B customers from year 2." },
        ],
      },
      de: {
        eyebrow: "Kalibrierzentrum Uetze",
        title: "Zertifizierte Ultrapräzision.",
        lede: "ISO/IEC 17025-Einrichtung für das Konsortium und Dritte (Automotive, Luftfahrt und Medizintechnik).",
        cards: [
          { t: "ISO-5-Reinraum",             d: "0,1 K thermische Stabilität, Vibrationen < 1 µm/s." },
          { t: "Laserinterferometrie",       d: "Rückführbar auf PTB Braunschweig." },
          { t: "Industrielle CT-Messtechnik", d: "Berührungslose Innenprüfung komplexer Teile." },
          { t: "Externer Service",           d: "Ab Jahr 2 offen für regionale B2B-Kunden." },
        ],
      },
    },
  },

  // -------------------------------------------------------------- 12
  {
    kind: "flow",
    id: "unified-flow",
    n: 12,
    i18n: {
      pt: {
        eyebrow: "O Ecossistema Unificado",
        title: "Cada camada fala com todas as outras.",
        lede: "Energia, automação e visão partilham o mesmo modelo digital em tempo real — a decisão numa camada altera imediatamente as restantes.",
        nodes: [
          { t: "Siemens",  s: "Energia · Rede · Nuvem" },
          { t: "VCOW",     s: "Controlo físico · Motion" },
          { t: "DFOPTIX",  s: "Visão · Metrologia · IA" },
          { t: "NeuNexus", s: "Orquestração · Governança" },
        ],
      },
      en: {
        eyebrow: "The Unified Ecosystem",
        title: "Every layer talks to every other.",
        lede: "Energy, automation and vision share the same live digital model — a decision on one layer immediately updates the others.",
        nodes: [
          { t: "Siemens",  s: "Energy · Grid · Cloud" },
          { t: "VCOW",     s: "Physical control · Motion" },
          { t: "DFOPTIX",  s: "Vision · Metrology · AI" },
          { t: "NeuNexus", s: "Orchestration · Governance" },
        ],
      },
      de: {
        eyebrow: "Das vereinte Ökosystem",
        title: "Jede Ebene spricht mit jeder anderen.",
        lede: "Energie, Automatisierung und Vision teilen dasselbe live-Digitalmodell — eine Entscheidung auf einer Ebene aktualisiert sofort die anderen.",
        nodes: [
          { t: "Siemens",  s: "Energie · Netz · Cloud" },
          { t: "VCOW",     s: "Physische Steuerung · Motion" },
          { t: "DFOPTIX",  s: "Vision · Messtechnik · KI" },
          { t: "NeuNexus", s: "Orchestrierung · Governance" },
        ],
      },
    },
  },

  // -------------------------------------------------------------- 13
  {
    kind: "mobility",
    id: "mobility-hub",
    n: 13,
    i18n: {
      pt: {
        eyebrow: "Mobility Hub de Uetze",
        title: "Devolvido à comunidade.",
        lede: "O consórcio inclui um centro de mobilidade elétrica integrado — não apenas para os seus trabalhadores, mas para toda a região de Peine/Hannover.",
        bullets: [
          "Terminal de autocarros 100 % elétricos ligado a Hannover e Braunschweig.",
          "24 pontos de carregamento rápido (350 kW) abertos ao público.",
          "Frota partilhada de veículos utilitários para PME locais.",
          "Aquecimento urbano (fernwärme) para o núcleo residencial de Uetze.",
        ],
      },
      en: {
        eyebrow: "Uetze Mobility Hub",
        title: "Given back to the community.",
        lede: "The consortium ships with an integrated e-mobility hub — not only for its own workers, but for the entire Peine/Hannover region.",
        bullets: [
          "Fully electric bus terminal connecting Hannover and Braunschweig.",
          "24 ultra-fast charging points (350 kW) open to the public.",
          "Shared utility-vehicle fleet available to local SMEs.",
          "District heating (Fernwärme) for the Uetze residential core.",
        ],
      },
      de: {
        eyebrow: "Uetze Mobility Hub",
        title: "Zurück in die Gemeinschaft.",
        lede: "Das Konsortium umfasst einen integrierten E-Mobility-Hub — nicht nur für die eigenen Mitarbeitenden, sondern für die gesamte Region Peine/Hannover.",
        bullets: [
          "Vollelektrisches Busterminal mit Anbindung Hannover ↔ Braunschweig.",
          "24 Schnellladepunkte (350 kW) öffentlich zugänglich.",
          "Geteilte Nutzfahrzeugflotte für lokale KMU.",
          "Fernwärme für den Wohnkern von Uetze.",
        ],
      },
    },
  },

  // -------------------------------------------------------------- 14
  {
    kind: "economics",
    id: "economics",
    n: 14,
    i18n: {
      pt: {
        eyebrow: "Retorno Económico Regional",
        title: "Uma nova base fiscal para a Baixa Saxónia.",
        lede: "Projeção conservadora do impacto direto e indireto do consórcio nos primeiros dez anos.",
        bars: [
          { l: "2026", v: 12 },
          { l: "2027", v: 28 },
          { l: "2028", v: 62 },
          { l: "2029", v: 110 },
          { l: "2030", v: 168 },
          { l: "2031", v: 218 },
          { l: "2032", v: 244 },
          { l: "2033", v: 268 },
          { l: "2034", v: 280 },
        ],
        yLabel: "Milhões € · PIB regional",
        notes: [
          "+320 empregos diretos · +1 200 indiretos.",
          "+18 % base fiscal municipal em Uetze até 2032.",
          "€42 M em contratos para PME da região por ano.",
        ],
      },
      en: {
        eyebrow: "Regional Economic Return",
        title: "A new tax base for Lower Saxony.",
        lede: "Conservative projection of the consortium's direct and indirect impact over the first ten years.",
        bars: [
          { l: "2026", v: 12 },
          { l: "2027", v: 28 },
          { l: "2028", v: 62 },
          { l: "2029", v: 110 },
          { l: "2030", v: 168 },
          { l: "2031", v: 218 },
          { l: "2032", v: 244 },
          { l: "2033", v: 268 },
          { l: "2034", v: 280 },
        ],
        yLabel: "Million € · regional GDP",
        notes: [
          "+320 direct jobs · +1,200 indirect.",
          "+18 % municipal tax base in Uetze by 2032.",
          "€42 M in contracts to regional SMEs per year.",
        ],
      },
      de: {
        eyebrow: "Regionale Wirtschaftsrendite",
        title: "Eine neue Steuerbasis für Niedersachsen.",
        lede: "Konservative Projektion der direkten und indirekten Konsortiumswirkung über die ersten zehn Jahre.",
        bars: [
          { l: "2026", v: 12 },
          { l: "2027", v: 28 },
          { l: "2028", v: 62 },
          { l: "2029", v: 110 },
          { l: "2030", v: 168 },
          { l: "2031", v: 218 },
          { l: "2032", v: 244 },
          { l: "2033", v: 268 },
          { l: "2034", v: 280 },
        ],
        yLabel: "Mio. € · Regionales BIP",
        notes: [
          "+320 direkte Arbeitsplätze · +1 200 indirekt.",
          "+18 % kommunale Steuerbasis in Uetze bis 2032.",
          "€42 Mio. Aufträge an regionale KMU pro Jahr.",
        ],
      },
    },
  },

  // -------------------------------------------------------------- 15
  {
    kind: "cta",
    id: "next-steps",
    n: 15,
    i18n: {
      pt: {
        eyebrow: "Próximos Passos",
        title: "Uma parceria para o futuro europeu.",
        lede: "O consórcio está estruturado, o site está pré-licenciado e o gémeo digital está pronto a arrancar. Falta apenas o parceiro estratégico.",
        timeline: [
          { d: "Q2 2026", t: "MoU de consórcio e comité diretor." },
          { d: "Q4 2026", t: "Groundbreaking do site de Uetze." },
          { d: "Q2 2028", t: "Primeira linha operacional." },
          { d: "2029",    t: "Certificação e serviço externo." },
        ],
        cta: {
          label: "Contactar o Consórcio Uetze",
          detail: "consortium@neunexus.energy · Uetze · Niedersachsen · Germany",
        },
      },
      en: {
        eyebrow: "Next Steps",
        title: "A partnership for the European future.",
        lede: "The consortium is structured, the site is pre-permitted and the digital twin is ready to run. Only the strategic partner is missing.",
        timeline: [
          { d: "Q2 2026", t: "Consortium MoU and steering committee." },
          { d: "Q4 2026", t: "Groundbreaking of the Uetze site." },
          { d: "Q2 2028", t: "First operational line." },
          { d: "2029",    t: "Certification and external service." },
        ],
        cta: {
          label: "Contact the Uetze Consortium",
          detail: "consortium@neunexus.energy · Uetze · Niedersachsen · Germany",
        },
      },
      de: {
        eyebrow: "Nächste Schritte",
        title: "Eine Partnerschaft für die europäische Zukunft.",
        lede: "Das Konsortium ist strukturiert, der Standort vorgenehmigt und der Digital Twin startklar. Es fehlt nur der strategische Partner.",
        timeline: [
          { d: "Q2 2026", t: "Konsortial-MoU und Steuerungskomitee." },
          { d: "Q4 2026", t: "Grundsteinlegung Standort Uetze." },
          { d: "Q2 2028", t: "Erste operative Linie." },
          { d: "2029",    t: "Zertifizierung und externer Service." },
        ],
        cta: {
          label: "Uetze-Konsortium kontaktieren",
          detail: "consortium@neunexus.energy · Uetze · Niedersachsen · Deutschland",
        },
      },
    },
  },
];

export const UI = {
  pt: {
    prev: "Anterior", next: "Próximo", slide: "Slide",
    consortium: "Consórcio · Uetze · Niedersachsen",
    intro: "Apresentação Executiva — Confidencial",
    back: "HannoNexus", play: "Reprodução automática",
    printedFrom: "Documento gerado a partir do Blueprint NeuNexus Energy · Uetze",
    blueprintRef: "Referência do Blueprint Original",
    blueprintHint: "Toque para ampliar a página original do documento técnico.",
    blueprintClose: "Fechar",
  },
  en: {
    prev: "Previous", next: "Next", slide: "Slide",
    consortium: "Consortium · Uetze · Lower Saxony",
    intro: "Executive Presentation — Confidential",
    back: "HannoNexus", play: "Auto-play",
    printedFrom: "Document rendered from the NeuNexus Energy · Uetze Blueprint",
    blueprintRef: "Original Blueprint Reference",
    blueprintHint: "Tap to enlarge the original technical document page.",
    blueprintClose: "Close",
  },
  de: {
    prev: "Zurück", next: "Weiter", slide: "Folie",
    consortium: "Konsortium · Uetze · Niedersachsen",
    intro: "Executive-Präsentation — Vertraulich",
    back: "HannoNexus", play: "Autoplay",
    printedFrom: "Dokument aus der NeuNexus Energy · Uetze Blaupause generiert",
    blueprintRef: "Original-Blaupause Referenz",
    blueprintHint: "Antippen, um die Originalseite des technischen Dokuments zu vergrößern.",
    blueprintClose: "Schließen",
  },
};

export const SUPPORTED = ["pt", "en", "de"];
